// One student session as a row, as /shares draws a share (ShareRow): its
// state as a dot, its kind as an icon, the student's name — under it the
// assignment it works on, whether it is late and the title its owner gave
// it (byj-share's default says nothing a person tells apart, so a row
// without an assignment says its tool and start instead); after the name a
// sign per kind of alert that still stands (AlertIcon), what each is and
// when in its title — and on the right its state, turns and cost and, apart,
// when it last did something. A permission it waits on is said once, in its
// state: the key's sign and how many, filled once the wait has run long
// enough to raise its alert — not again beside its name. The time, on
// a narrow list stays on the name's line. On the dashboard a row is one
// thing to press: anywhere on it, its name a button for a keyboard, it is
// watched live beside the rows, from where its session opens. Elsewhere (the
// assignment page, titled by the session, the student being the row above
// it) its title is the link to the session. The relay's list says when a
// session last did something, not when it started, so that is its time.
import { memo } from "react";
import { useTranslation } from "react-i18next";
import type { ClassSession } from "../api";
import { alertChips, byPressing, sessionLabel } from "../classroom";
import { kindIcon, kindOf } from "../kinds";
import { AlertIcon, alertTone } from "./AlertIcon";
import { t, ago, money, when, word } from "../i18n";
import { Link } from "./Nav";
import { SelectBox } from "./Selection";

export const ClassSessionRow = memo(function ClassSessionRow({ s, now, titled = "student", selected = false, onSelect, picking = false, picked = false, onPick }: {
  s: ClassSession;
  now: number;
  /** What the row's title reads: the student's name, or the session's title. */
  titled?: "student" | "session";
  /** Whether the dashboard is watching this session. */
  selected?: boolean;
  /** Given, a press on the row or its title watches it; not, the title is the link to the session. */
  onSelect?: (s: ClassSession) => void;
  /** While the list is being selected from: its box, whether it is picked, and what a press on it does — plain values and a stable callback, so the memo holds. */
  picking?: boolean;
  picked?: boolean;
  onPick?: (share: string) => void;
}) {
  // under memo its props do not change with the language, so it listens for it (i18n/Localized)
  useTranslation();
  const k = kindOf(s.source);
  const over = s.state === "ended" || !s.live;
  const all = alertChips(s.alerts).sort((a, b) => byPressing(a.kind, b.kind));
  // the permission's wait is said by the state (below), the rest by their signs
  const waitedLong = all.find((c) => c.kind === "permission-wait");
  const chips = all.filter((c) => c !== waitedLong);
  const waiting = s.pending_permissions > 0 && !over;
  const label = sessionLabel(s.title, s.share, undefined, new Date(now));
  const title = titled === "student" ? s.student.name : label.text;
  // under a student's name: the title its owner gave, or with no assignment to say what it is, its tool and start
  const sub = titled === "student" && (label.named || !s.assignment) ? label.text : null;
  // what a pointer reads in the alert chips' and the time's titles, which a
  // touch screen cannot show: a line of its own there (.row-detail)
  const detail = [
    ...all.filter((c) => c.detail).map((c) => `${word("session:alertKind", c.kind) ?? c.kind}: ${c.detail}`),
    s.last_event_at ? t("classes:lastEventAt", { at: when(s.last_event_at) }) : null,
  ].filter(Boolean);
  return (
    <li
      className={`share-row class-row state-${s.state} ${over ? "is-ended" : "is-live"}${all.length && !over ? " has-alerts" : ""}${selected ? " is-selected" : ""}${onSelect ? " is-pickable" : ""} no-menu${picking && picked ? " is-picked" : ""}`}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect ? (e) => !(e.target as HTMLElement).closest("a, button, input, [role=menu], .row-menu") && onSelect(s) : undefined}
    >
      <span className="row-lead">
        {picking && onPick && <SelectBox id={s.share} title={title} picked={picked ? new Set([s.share]) : new Set()} toggle={onPick} />}
        <span className="state-dot" aria-hidden="true" />
      </span>
      <span className={`kind-box kind-${k}`} aria-hidden="true" title={word("shares:kindName", k) ?? k}>
        {kindIcon[k]}
      </span>
      <div className="row-main">
        <div className="row-title-line">
          {onSelect ? (
            <button type="button" className="row-title row-title-button" aria-pressed={selected} aria-label={t("classes:watchOf", { name: title })} onClick={() => onSelect(s)}>
              {title}
            </button>
          ) : (
            <Link to={`/s/${encodeURIComponent(s.share)}`} className="row-title">
              {title}
            </Link>
          )}
          {chips.length > 0 && (
            <span className="alert-badges">
              {chips.map((c) => (
                <span
                  key={c.kind}
                  className={`alert-badge tone-${alertTone(c.kind)}`}
                  role="img"
                  aria-label={`${word("session:alertKind", c.kind) ?? c.kind}${c.count > 1 ? ` ×${c.count}` : ""}`}
                  title={[`${word("session:alertKind", c.kind) ?? c.kind}${c.count > 1 ? ` ×${c.count}` : ""}`, c.detail, ago(c.at, now)].filter(Boolean).join(" · ")}
                  data-kind={c.kind}
                >
                  <AlertIcon kind={c.kind} />
                  {c.count > 1 && <span className="alert-badge-count">{c.count}</span>}
                </span>
              ))}
            </span>
          )}
        </div>
        {(sub || s.assignment || s.late) && (
          <div className="row-sub" title={[s.assignment?.title, sub].filter(Boolean).join(" · ")}>
            {s.assignment && <span data-testid="assignment">{s.assignment.title}</span>}
            {s.late && (
              <>
                {" "}
                <span className="chip chip-warn" data-testid="late">
                  {t("classes:late")}
                </span>
              </>
            )}
            {sub && (
              <>
                {s.assignment && " · "}
                {sub}
              </>
            )}
          </div>
        )}
      </div>
      {detail.length > 0 && <div className="row-detail">{detail.join(" · ")}</div>}
      <div className="row-meta">
        <span
          className={`chip state-chip state-chip-${s.state}${waiting ? " is-waiting" : ""}${waiting && waitedLong ? " waited-long" : ""}`}
          data-testid="state"
          title={waiting ? [t("classes:pendingN", { count: s.pending_permissions }), waitedLong?.detail].filter(Boolean).join(" · ") : undefined}
        >
          {waiting && <AlertIcon kind="permission-wait" size={12} />}
          {word("classes:stateBadge", s.state) ?? s.state}
          {waiting && s.pending_permissions > 1 && <span className="state-chip-count"> ×{s.pending_permissions}</span>}
          {waiting && <span className="sr-only"> · {t("classes:pendingN", { count: s.pending_permissions })}</span>}
        </span>
        <span className="row-stats">
          <span>{t("classes:turnsN", { count: s.turns })}</span>
          <span>{money(s.cost_usd)}</span>
        </span>
      </div>
      {/* empty for a session that has sent nothing yet; the column stays */}
      <time className="row-time" dateTime={s.last_event_at || undefined} title={s.last_event_at ? t("classes:lastEventAt", { at: when(s.last_event_at) }) : undefined}>
        {s.last_event_at ? ago(s.last_event_at, now) : ""}
      </time>
    </li>
  );
});
